import React, { SyntheticEvent, useContext, useId } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { Icon } from '@fikasio/react-ui-components';
import UserAvatar from 'components/UI/UserAvatar';
import { OpenStatusDropdownContext } from 'components/tasks/TasksViewer/TaskStatusDropdown';
import { useAuth } from 'services/login/hooks';
import { useCurrentOrganizationUsersQuery } from 'services/organizations/api';
import { assigneeDisplayName, withCurrentAssignableUser } from 'services/users/assignable';
import { ListedUser } from 'services/users/types';
import styles from './TasksList.module.css';

interface AssigneeToggleProps {
  'aria-expanded'?: boolean;
  assignee?: ListedUser;
  assigneeId?: string;
  className?: string;
  onClick?: (event: SyntheticEvent) => void;
  style?: React.CSSProperties;
}

const AssigneeToggle = React.forwardRef<HTMLDivElement, AssigneeToggleProps>(({
  'aria-expanded': ariaExpanded,
  assignee,
  assigneeId,
  className,
  onClick,
  style,
  ...props
}, ref) => {
  const { t } = useTranslation();

  return (
    <div
      {...props}
      aria-expanded={ariaExpanded}
      aria-haspopup="menu"
      aria-label={assignee ? assigneeDisplayName(assignee) : t('assignee')}
      className={`${styles.taskRowAssigneeToggle}${className ? ` ${className}` : ''}`}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
      ref={ref}
      role="button"
      style={style}
      tabIndex={0}
      title={assignee ? assigneeDisplayName(assignee) : t('assignee')}
    >
      {
        assigneeId
          ? (
            <UserAvatar
              alt={assignee ? assigneeDisplayName(assignee) : t('assignee')}
              hasProfilePicture={assignee?.hasProfilePicture}
              modifiedAt={assignee?.modifiedAt}
              size={23}
              userId={assigneeId}
            />
          ) : (
            <Icon
              name="user"
              size="1x"
              style={{
                color: '#cecece',
                fontSize: 14,
              }}
            />
          )
      }
    </div>
  );
});

AssigneeToggle.displayName = 'AssigneeToggle';

interface AssigneeButtonProps {
  assigneeId?: string;
  onChange: (assigneeId: string | null) => void;
  style?: React.CSSProperties;
}

const AssigneeButton = ({
  assigneeId,
  onChange,
  style,
}: AssigneeButtonProps) => {
  const { t } = useTranslation();
  const dropdownId = useId();
  const exclusiveDropdown = useContext(OpenStatusDropdownContext);
  const { user: currentUser } = useAuth();
  const { data: assignableUsers } = useCurrentOrganizationUsersQuery();
  const users = withCurrentAssignableUser(assignableUsers, currentUser);
  const assignee = users.find(user => user.id === assigneeId);

  return (
    <Dropdown
      className={styles.assigneeButton}
      {...(exclusiveDropdown && {
        onToggle: (nextShow: boolean) => {
          exclusiveDropdown.setOpenDropdownId(nextShow ? dropdownId : null);
        },
        show: exclusiveDropdown.openDropdownId === dropdownId,
      })}
    >
      <Dropdown.Toggle
        as={AssigneeToggle as React.ElementType}
        assignee={assignee}
        assigneeId={assigneeId}
        style={style}
      />
      <Dropdown.Menu
        data-testid="task-assignee-menu"
        popperConfig={{
          modifiers: [{ name: 'hide', enabled: false }],
        }}
      >
        <Dropdown.Item
          active={!assigneeId}
          onClick={() => onChange(null)}
        >
          <Icon
            name="user"
            style={{
              color: '#cecece',
              flexShrink: 0,
              marginRight: 10,
              width: 20,
            }}
          />
          {t('unassigned')}
        </Dropdown.Item>
        {
          (users).map(user => (
            <Dropdown.Item
              active={assigneeId === user.id}
              key={user.id}
              onClick={() => onChange(user.id)}
            >
              <UserAvatar
                alt=""
                hasProfilePicture={user.hasProfilePicture}
                modifiedAt={user.modifiedAt}
                size={20}
                style={{
                  flexShrink: 0,
                  marginRight: 10,
                }}
                userId={user.id}
              />
              {assigneeDisplayName(user)}
            </Dropdown.Item>
          ))
        }
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default AssigneeButton;
